import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { LightbulbLit } from '@/resources/assets/images';
import OnboardingSuccessModal from './OnboardingSuccessModal';
import OnboardingFailureModal from './OnboardingFailureModal';
import OnboardingLoadingModal from './OnboardingLoadingModal';
import { useOnboardingContext } from '../context/OnboardingContext';
import { useCreateOnboardingUser } from '../hooks/useCreateOnboardingUser';
import { useCreateOnboardingList } from '../hooks/useCreateOnboardingList';
import { useCreateAiSequence } from '../hooks/useCreateAiSequence';
import { useUpdateOnboardingStatus } from '../hooks/useUpdateOnboardingStatus';
import { useCreateOnboardingScore } from '../hooks/useCreateOnboardingScore';

type Step = 'user' | 'list' | 'ai' | null;

export default function OnboardingGenerating() {
	const [generationStatus, setGenerationStatus] = useState<
		'success' | 'fail' | null
	>(null);
	const [isMinimumLoadingComplete, setIsMinimumLoadingComplete] =
		useState(false);
	const [isRetrying, setIsRetrying] = useState(false);
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [currentStep, setCurrentStep] = useState<Step>('user');
	const { onboardingData } = useOnboardingContext();
	const history = useHistory();

	const { mutateAsync: createUserOnboarding } = useCreateOnboardingUser();
	const { mutateAsync: createOnboardingList } = useCreateOnboardingList();
	const { mutateAsync: createAiSequence } = useCreateAiSequence();
	const { mutateAsync: updateOnboardingStatus } = useUpdateOnboardingStatus();
	const { mutateAsync: createOnboardingScore } = useCreateOnboardingScore();

	const { targetAudience, offering, valueProposition, signature } =
		onboardingData;

	const MINIMUM_LOADING_TIME_MS = 11250;
	const MAXIMUM_ALLOWED_TIME_MS = 50000;
	const SUCCESS_MODAL_DELAY_MS = 3750;
	const REDIRECT_DELAY_MS = 8000;
	const RETRY_ANIMATION_DELAY_MS = 375;

	const startGeneration = async (step: Step = 'user') => {
		setGenerationStatus(null);
		setIsMinimumLoadingComplete(false);
		setIsRetrying(false);

		const startTime = Date.now();

		try {
			let elapsedTime = 0;
			let listId = '';

			if (step === 'user') {
				await createUserOnboarding({
					targetAudience,
					offering,
					valueProposition,
					signature
				});

				await createOnboardingScore({});

				setCurrentStep('list');

				elapsedTime = Date.now() - startTime;
			}

			if (step === 'user' || step === 'list') {
				const onboardingListResponse = await createOnboardingList({
					targetAudience,
					offering
				});

				setCurrentStep('ai');

				elapsedTime = Date.now() - startTime;

				listId = onboardingListResponse.data.data.listId;
			}

			if (step === 'user' || step === 'list' || step === 'ai') {
				await createAiSequence({
					targetAudience,
					offering,
					valueProposition,
					signature
				});

				setCurrentStep(null);
				elapsedTime = Date.now() - startTime;
			}

			if (elapsedTime > MAXIMUM_ALLOWED_TIME_MS) {
				throw new Error('Generation process timed out');
			}

			const remainingLoadingTime = Math.max(
				0,
				MINIMUM_LOADING_TIME_MS - elapsedTime
			);

			setTimeout(() => {
				setIsMinimumLoadingComplete(true);
			}, remainingLoadingTime);

			setGenerationStatus('success');

			setTimeout(() => {
				sessionStorage.setItem('suppressOnboardingRedirect', 'true');
				history.push(`/sequences?state=STEPS-CREATION&id=${listId}`);
			}, remainingLoadingTime + REDIRECT_DELAY_MS);
		} catch (error) {
			setGenerationStatus('fail');

			setIsMinimumLoadingComplete(true);
		}
	};

	useEffect(() => {
		startGeneration(currentStep);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (generationStatus === 'success' && isMinimumLoadingComplete) {
			const successModalTimer = setTimeout(() => {
				setShowSuccessModal(true);
			}, SUCCESS_MODAL_DELAY_MS);

			return () => clearTimeout(successModalTimer);
		}
	}, [generationStatus, isMinimumLoadingComplete]);

	const handleRetry = () => {
		setIsRetrying(true);
		startGeneration(currentStep);
		setShowSuccessModal(false);

		setTimeout(() => {
			setIsRetrying(false);
		}, RETRY_ANIMATION_DELAY_MS);
	};

	const handleSkip = () => {
		updateOnboardingStatus({
			status: 'skipped'
		}).then(() => {
			sessionStorage.setItem('suppressOnboardingRedirect', 'true');
			history.push('/search-businesses');
		});
	};

	if (generationStatus === 'fail' && isMinimumLoadingComplete) {
		return (
			<div className="h-full w-full overflow-hidden rounded-2xl border border-l border-dm-gray">
				<HeaderContent />
				<OnboardingFailureModal
					isRetrying={isRetrying}
					handleRetry={handleRetry}
					onSkip={handleSkip}
				/>
			</div>
		);
	}

	if (showSuccessModal) {
		return (
			<div className="h-full w-full overflow-hidden rounded-2xl border border-l border-dm-gray">
				<HeaderContent />
				<OnboardingSuccessModal
					minimumTimePassed={isMinimumLoadingComplete}
				/>
			</div>
		);
	}

	return (
		<div className="h-full w-full overflow-hidden rounded-2xl border border-l border-dm-gray">
			<HeaderContent />
			<OnboardingLoadingModal
				minimumTimePassed={isMinimumLoadingComplete}
				apiResult={generationStatus}
			/>
		</div>
	);
}

function HeaderContent() {
	return (
		<div className="h-[4.75rem] w-full overflow-hidden rounded-tl-lg border-b border-l border-t border-dm-gray px-4 sm:h-[6.375rem] sm:px-20">
			<div className="flex h-full w-full items-center">
				<div className="mx-auto flex w-full max-w-[79rem] items-center justify-center gap-4">
					<div className="shrink-0">
						<LightbulbLit className="h-[2rem] w-[1.375rem] shrink-0" />
					</div>
					<h1 className="font-barlow text-[0.875rem] font-semibold leading-[1.375rem] text-dm-white sm:text-[1.125rem]">
						Did you know: 76% of all generated leads come from the
						first, second, or third follow-up—not the initial
						outreach.
					</h1>
				</div>
			</div>
		</div>
	);
}
